import type { ReportFilterSelectOption } from '../shared/report-filter-select-option.model';
import {
    REPORT_RECORD_COUNT_ALLOWED_NUMBERS,
    REPORT_RECORD_COUNT_ELEMENT_ID,
} from '../agreements/report-record-count.constants';

/** Get-RegulatoryReports — DWT payloads sent to Trace (Excel filter on `data.filters`). */
export const DWT_PAYLOADS_ACTIVITY = 'Get-RegulatoryReports' as const;

/** Excel structured table name for DWT payload rows. */
export const DWT_PAYLOADS_TABLE_NAME = 'TraceDwtPayloadsToTrace';

/** Task pane facility multiselect; options come from Get-UserInfo. */
export const DWT_PAYLOADS_FACILITY_CODE_ELEMENT_ID = 'filter-dwt-facility-code';

export const DWT_PAYLOADS_STATUS_ELEMENT_ID = 'filter-dwt-status';

export const DWT_PAYLOADS_START_DATE_ELEMENT_ID = 'filter-dwt-start-date';

export const DWT_PAYLOADS_END_DATE_ELEMENT_ID = 'filter-dwt-end-date';

export const DWT_PAYLOADS_RECORD_COUNT_ELEMENT_ID = REPORT_RECORD_COUNT_ELEMENT_ID;

/** Inclusive last-N-day window used to prefill start/end when the DWT target is shown. */
export const DWT_PAYLOADS_DEFAULT_LOOKBACK_DAYS = 7;

export const DWT_PAYLOADS_RECORD_COUNT_DEFAULT_NUM = 5_000;

export const DWT_PAYLOADS_RECORD_COUNT_DEFAULT_VALUE = '5000';

export const DWT_PAYLOADS_RECORD_COUNT_ALLOWED_NUMBERS: readonly number[] =
    REPORT_RECORD_COUNT_ALLOWED_NUMBERS;

export const DWT_PAYLOADS_RECORD_COUNT_SELECT_OPTIONS: readonly ReportFilterSelectOption[] =
    DWT_PAYLOADS_RECORD_COUNT_ALLOWED_NUMBERS.map((n) => ({
        value: String(n),
        label: String(n),
    }));

export const DWT_PAYLOAD_STATUS_SUCCESSFUL = 'Successful' as const;
export const DWT_PAYLOAD_STATUS_WARNINGS = 'Warnings' as const;
export const DWT_PAYLOAD_STATUS_ERRORS = 'Errors' as const;

export type DwtPayloadReportStatus =
    | typeof DWT_PAYLOAD_STATUS_SUCCESSFUL
    | typeof DWT_PAYLOAD_STATUS_WARNINGS
    | typeof DWT_PAYLOAD_STATUS_ERRORS;

/** BE `RegulatorySubmissionStatus`: 2 Success, 3 Retrying (Warnings), 4 Failed. */
export const DWT_PAYLOAD_STATUS_CODE_SUCCESSFUL = 2;
export const DWT_PAYLOAD_STATUS_CODE_WARNINGS = 3;
export const DWT_PAYLOAD_STATUS_CODE_FAILED = 4;

export type DwtPayloadReportStatusCode =
    | typeof DWT_PAYLOAD_STATUS_CODE_SUCCESSFUL
    | typeof DWT_PAYLOAD_STATUS_CODE_WARNINGS
    | typeof DWT_PAYLOAD_STATUS_CODE_FAILED;

export const DWT_PAYLOAD_REPORT_STATUS_CODES: readonly DwtPayloadReportStatusCode[] = [
    DWT_PAYLOAD_STATUS_CODE_SUCCESSFUL,
    DWT_PAYLOAD_STATUS_CODE_WARNINGS,
    DWT_PAYLOAD_STATUS_CODE_FAILED,
];

/** Task-pane checkbox labels for BE status integers. */
export const DWT_PAYLOAD_STATUS_CODE_TO_FILTER_LABEL: Record<
    DwtPayloadReportStatusCode,
    DwtPayloadReportStatus
> = {
    [DWT_PAYLOAD_STATUS_CODE_SUCCESSFUL]: DWT_PAYLOAD_STATUS_SUCCESSFUL,
    [DWT_PAYLOAD_STATUS_CODE_WARNINGS]: DWT_PAYLOAD_STATUS_WARNINGS,
    [DWT_PAYLOAD_STATUS_CODE_FAILED]: DWT_PAYLOAD_STATUS_ERRORS,
};

export const DWT_PAYLOAD_REPORT_STATUS_CODE_VALUES: readonly string[] =
    DWT_PAYLOAD_REPORT_STATUS_CODES.map((code) => String(code));

export function resolveDwtPayloadsRecordCount(value: number | undefined): number {
    if (typeof value === 'number' && DWT_PAYLOADS_RECORD_COUNT_ALLOWED_NUMBERS.includes(value)) {
        return value;
    }
    return DWT_PAYLOADS_RECORD_COUNT_DEFAULT_NUM;
}

export function isDwtPayloadReportStatusCode(value: number): value is DwtPayloadReportStatusCode {
    return (DWT_PAYLOAD_REPORT_STATUS_CODES as readonly number[]).includes(value);
}

/** Checkbox values `"2"` / `"3"` / `"4"` from the DWT status multiselect */
export function dwtPayloadStatusCodesFromMultiselectValues(selected: string[]): number[] {
    const codes = selected
        .map((value) => Number(value.trim()))
        .filter((n): n is DwtPayloadReportStatusCode => isDwtPayloadReportStatusCode(n));
    return Array.from(new Set(codes));
}
